/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useCampaignManager } from '../../communication/CampaignContext';
import { ProgrammableSurfacesCommunication } from '../../communication/ProgrammableSurfacesCommunication';
import { ProgrammableSurfacesEvents } from '../../communication/types';
import { ActionCardWithFeedback } from '../../components/ActionsCardWithFeedback/ActionsCardWithFeedback';
import { strings } from '../../intl/Strings';
import { logger } from '../../shared';
import {
    CampaignConfig,
    CampaignStateResponsePayload,
    isCampaignStatePayload
} from '../../types/CampaignTypes';
import { LftSidePaneBrainstormAction } from './components/actions/LftSidePaneBrainstormAction';
import { LftSidePaneCompareFilesAction } from './components/actions/LftSidePaneCompareFileAction';
import { LftSidePaneCreateImageAction } from './components/actions/LftSidePaneCreateImageAction';
import { LftSidePaneWhatCanIDoAction } from './components/actions/LftSidePaneWhatCanIDoAction';
import { LftSidePaneActionId } from './components/shared';

/**
 * Campaign configuration for LFT side pane card campaign.
 * Defines the 4 required actions and maps Win32 schema IDs to ActionId enum.
 */
const SIDEPANE_CAMPAIGN_CONFIG: CampaignConfig<LftSidePaneActionId> = {
    componentId: 'lft-sidepane-card',
    requiredActions: [
        LftSidePaneActionId.WhatCanIDo,
        LftSidePaneActionId.Brainstorm,
        LftSidePaneActionId.CreateImage,
        LftSidePaneActionId.CompareFiles
    ],
    /**
     * Maps action ID strings from Win32 schema to ActionId enum values.
     * Performs case-insensitive matching to handle mixed casing from Win32.
     */
    actionMapper: (schemaActionId: string): LftSidePaneActionId | null => {
        const normalized = schemaActionId.toLowerCase().trim();

        switch (normalized) {
            case 'whatcanido':
                return LftSidePaneActionId.WhatCanIDo;
            case 'brainstorm':
                return LftSidePaneActionId.Brainstorm;
            case 'createimage':
                return LftSidePaneActionId.CreateImage;
            case 'comparefile':
            case 'comparefiles':
                return LftSidePaneActionId.CompareFiles;
            default:
                // Unknown action IDs are ignored with warning
                console.warn(`[LftSidePaneCard] Unknown action ID ignored: ${schemaActionId}`);
                return null;
        }
    }
};

export const LftSidePaneExperiment = () => {
    const titleLines = [strings.getString('lftSidePaneTitle')];

    // Campaign state: tracks which actions are completed
    const [campaignState, setCampaignState] = useState<Set<LftSidePaneActionId>>(new Set());
    const [campaignStateReceived, setCampaignStateReceived] = useState(false);

    // Get campaign manager from React Context
    const campaignManager = useCampaignManager(SIDEPANE_CAMPAIGN_CONFIG);

    const communication = ProgrammableSurfacesCommunication.getInstance();

    const sendHandoffEvent = (featureName: string, prompt: string) => {
        console.log('[LftSidePaneCard] Sending handoff - featureName:', featureName, 'prompt:', prompt);
        communication.sendEvent({
            eventType: ProgrammableSurfacesEvents.Handoff,
            payload: {
                featureName: featureName,
                prompt: prompt
            }
        });
    };

    // Campaign state initialization - Request-Response pattern
    useEffect(() => {
        const communication = ProgrammableSurfacesCommunication.getInstance();

        // Handler for campaign state response from Win32
        const handleCampaignStateResponse = (payload: CampaignStateResponsePayload) => {
            try {
                // Validate incoming data
                if (!isCampaignStatePayload(payload)) {
                    console.error('[LftSidePaneCard] Invalid CampaignStatePayload received:', payload);
                    return;
                }

                logger.logInfo('CampaignStateResponse', 'Campaign state response received from Win32');

                // Initialize campaign state in manager
                campaignManager.initializeCampaignState(payload);

                // Extract completed actions from manager
                const completedActions = campaignManager.getCompletedActions();

                // Update UI state to show completed actions
                setCampaignState(completedActions);
                setCampaignStateReceived(true);

                logger.logInfo(
                    'CampaignStateInitialized',
                    `Initialized with ${completedActions.size} completed actions`
                );
            } catch (error) {
                console.error('[LftSidePaneCard] Error handling campaign state response:', error);
            }
        };

        // Listen for response event
        communication.addEventListener<CampaignStateResponsePayload>(
            ProgrammableSurfacesEvents.CampaignStateResponse,
            handleCampaignStateResponse
        );

        // Request campaign state from Win32
        logger.logInfo('RequestCampaignState', 'Requesting campaign state from Win32');
        campaignManager.requestCampaignState();

        // Cleanup on unmount
        return () => {
            communication.removeEventListener<CampaignStateResponsePayload>(
                ProgrammableSurfacesEvents.CampaignStateResponse,
                handleCampaignStateResponse
            );
        };
    }, [campaignManager]);

    /**
     * Handles action click - updates campaign state via communication bridge and sends handoff event.
     * Campaign state is synchronized with Win32 to track user progress across sessions.
     */
    const handleTaskClick = (actionId: LftSidePaneActionId, featureName: string, prompt: string) => {
        // Update campaign state if action not yet completed
        if (!campaignState.has(actionId)) {
            const newState = new Set(campaignState);
            newState.add(actionId);
            setCampaignState(newState);
            campaignManager.completeAction(actionId);
        }

        // Send handoff event to Win32 to trigger action
        sendHandoffEvent(featureName, prompt);
    };

    // Recreate actions when campaignState updates to refresh checked status
    const actions = React.useMemo(() => [
        {
            ...LftSidePaneWhatCanIDoAction(() => handleTaskClick(
                LftSidePaneActionId.WhatCanIDo,
                'lftwhatcando',
                strings.getString('lftSidePaneCopilotContent')
            )),
            checked: campaignState.has(LftSidePaneActionId.WhatCanIDo)
        },
        {
            ...LftSidePaneBrainstormAction(() => handleTaskClick(
                LftSidePaneActionId.Brainstorm,
                'lftbrainstorm',
                strings.getString('lftSidePaneBrainstormContent')
            )),
            checked: campaignState.has(LftSidePaneActionId.Brainstorm)
        },
        {
            ...LftSidePaneCreateImageAction(() => handleTaskClick(
                LftSidePaneActionId.CreateImage,
                'lftcreateimg',
                strings.getString('lftSidePaneCreateImageContent')
            )),
            checked: campaignState.has(LftSidePaneActionId.CreateImage)
        },
        {
            ...LftSidePaneCompareFilesAction(() => handleTaskClick(
                LftSidePaneActionId.CompareFiles,
                'lftcomparefil',
                strings.getString('lftSidePaneCompareFilesContent')
            )),
            checked: campaignState.has(LftSidePaneActionId.CompareFiles)
        }
    ], [campaignState]);

    return (
        <ActionCardWithFeedback titleLines={titleLines} actions={actions} />
    );
};
